import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HubLayout from '../../components/HubLayout';
import MetricCard from '../../components/MetricCard';
import { api } from '../../api/client';

// Import existing inventory components
import InventoryByLocation from './InventoryByLocation';
import ProductMaster from '../../components/ProductMaster';
import ProductDetailPage from '../../components/ProductDetailPage';

const InventoryHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // ============================================================
  // LIFTED STATE: Product Edit Modal
  // Moved from ProductMaster to prevent state loss on re-renders
  // ============================================================
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  
  // Handler passed down to ProductMaster
  const handleEditProduct = (productId) => {
    console.log('📝 InventoryHub: Opening edit modal for product ID:', productId);
    setSelectedProductId(productId);
    setEditModalOpen(true);
  };
  
  const handleCloseEditModal = () => {
    console.log('🔴 InventoryHub: Closing edit modal');
    setEditModalOpen(false);
    setSelectedProductId(null);
  };
  // ============================================================
  
  const [inventoryData, setInventoryData] = useState({
    fba: { qty: 0, value: 0 },
    awd: { qty: 0, value: 0 },
    gar: { qty: 0, value: 0 },
    off: { qty: 0, value: 0 },
    total: { qty: 0, value: 0 },
    lowStock: 0,
    totalSKUs: 0
  });
  
  // ============================================================
  // COMPONENT-BASED INVENTORY: Product Type Stats
  // ============================================================
  const [componentInventory, setComponentInventory] = useState({
    rawMaterials: 0,
    finishedGoods: 0,
    standardProducts: 0,
    lowStockRaw: 0
  });

  // Fetch inventory metrics from API
  const fetchInventoryMetrics = async () => {
    try {
      setLoading(true);
      
      // Fetch inventory locations data
      const locationsResponse = await api.get('/api/v1/inventory/locations', {
        headers: { 'X-Tenant-ID': 'tenant-123' }
      });
      const locations = locationsResponse || [];
      
      // Fetch product master for pricing data
      const productData = await api.get('/api/v1/product-master/list?tenant_id=tenant-123&page_size=500');
      const products = productData.items || [];
      
      // Create a price lookup map
      const priceMap = {};
      products.forEach(p => {
        const cost = p.pricing_v2?.total_cost || p.effective_cost || p.cost_price || 0;
        priceMap[p.sku] = cost;
      });
      
      // Calculate metrics by location
      let fbaQty = 0, fbaValue = 0;
      let awdQty = 0, awdValue = 0;
      let garQty = 0, garValue = 0;
      let offQty = 0, offValue = 0;
      let lowStockCount = 0;
      
      locations.forEach(item => {
        const cost = priceMap[item.sku] || item.cogs || 0;
        const locs = item.locations || {};
        
        // FBA - clamp to 0 minimum (negative means no initial stock was set)
        const fba = Math.max(0, locs.FBA || 0);
        fbaQty += fba;
        fbaValue += fba * cost;
        
        // AWD - clamp to 0 minimum
        const awd = Math.max(0, locs.AWD || 0);
        awdQty += awd;
        awdValue += awd * cost;
        
        // GAR (Garage/Warehouse) - clamp to 0 minimum
        const gar = Math.max(0, locs.GAR || 0);
        garQty += gar;
        garValue += gar * cost;
        
        // OFF (Offsource 3PL) - clamp to 0 minimum
        const off = Math.max(0, locs.OFF || 0);
        offQty += off;
        offValue += off * cost;
        
        // Check low stock (total < 10)
        const totalQty = Math.max(0, item.total_qty || (fba + awd + gar + off));
        if (totalQty > 0 && totalQty < 10) {
          lowStockCount++;
        }
      });
      
      const totalQty = fbaQty + awdQty + garQty + offQty;
      const totalValue = fbaValue + awdValue + garValue + offValue;
      
      setInventoryData({
        fba: { qty: fbaQty, value: fbaValue },
        awd: { qty: awdQty, value: awdValue },
        gar: { qty: garQty, value: garValue },
        off: { qty: offQty, value: offValue },
        total: { qty: totalQty, value: totalValue },
        lowStock: lowStockCount,
        totalSKUs: locations.length
      });
      
      // ============================================================
      // COMPONENT-BASED INVENTORY: Fetch product type stats
      // ============================================================
      try {
        const componentData = await api.get('/api/v1/product-master/products-with-availability?tenant_id=tenant-123');
        const allProducts = componentData.products || [];
        
        const rawMaterials = allProducts.filter(p => p.product_type === 'raw_material');
        const finishedGoods = allProducts.filter(p => p.product_type === 'finished_product');
        const standardProducts = allProducts.filter(p => p.product_type === 'standard' || !p.product_type);
        
        // Count low stock raw materials
        const lowStockRaw = rawMaterials.filter(p => {
          const stock = p.current_stock || 0;
          const reorderPoint = p.reorder_point || 50;
          return stock < reorderPoint;
        }).length;
        
        // Count low stock finished goods
        const lowStockFinished = finishedGoods.filter(p => {
          const available = p.available_to_sell || 0;
          return available < 10;
        }).length;
        
        setComponentInventory({
          rawMaterials: rawMaterials.length,
          finishedGoods: finishedGoods.length,
          standardProducts: standardProducts.length,
          lowStockRaw: lowStockRaw + lowStockFinished
        });
      } catch (componentError) {
        console.error('Error fetching component inventory:', componentError);
      }
      
    } catch (error) {
      console.error('Error fetching inventory metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryMetrics();
  }, []);

  // Format currency
  const formatValue = (value) => {
    const numValue = Number(value) || 0;
    if (numValue >= 1000) {
      return `$${(numValue / 1000).toFixed(1)}K`;
    }
    return `$${numValue.toFixed(0)}`;
  };

  // Metric cards configuration
  const metrics = [
    // ============================================================
    // COMPONENT-BASED INVENTORY: New summary cards
    // ============================================================
    {
      icon: '🧱',
      label: 'RAW',
      value: componentInventory.rawMaterials.toString(),
      subtitle: 'Materials',
      status: componentInventory.rawMaterials > 0 ? 'default' : 'default',
      color: 'orange'
    },
    {
      icon: '🎁',
      label: 'FINISHED',
      value: componentInventory.finishedGoods.toString(),
      subtitle: 'Goods',
      status: componentInventory.finishedGoods > 0 ? 'default' : 'default',
      color: 'green'
    },
    {
      icon: '📦',
      label: 'FBA',
      value: inventoryData.fba.qty.toString(),
      subtitle: formatValue(inventoryData.fba.value),
      status: inventoryData.fba.qty > 0 ? 'green' : 'default'
    },
    {
      icon: '🚚',
      label: 'AWD',
      value: inventoryData.awd.qty.toString(),
      subtitle: formatValue(inventoryData.awd.value),
      status: inventoryData.awd.qty > 0 ? 'green' : 'default'
    },
    {
      icon: '💰',
      label: 'TOTAL',
      value: formatValue(inventoryData.total.value),
      subtitle: `${inventoryData.total.qty} units`,
      status: 'default'
    },
    {
      icon: '⚠️',
      label: 'LOW STOCK',
      value: (inventoryData.lowStock + componentInventory.lowStockRaw).toString(),
      subtitle: 'Need reorder',
      status: (inventoryData.lowStock + componentInventory.lowStockRaw) === 0 ? 'green' : (inventoryData.lowStock + componentInventory.lowStockRaw) < 5 ? 'yellow' : 'red',
      action: (inventoryData.lowStock + componentInventory.lowStockRaw) > 0 ? 'Fix' : undefined
    }
  ];

  // Tab configuration
  const tabs = [
    { id: 'location', label: 'By Location', icon: '📍' },
    { id: 'products', label: 'Products', icon: '📦' },
    { id: 'overview', label: 'Overview', icon: '📊' }
  ];

  const actions = (
    <button 
      onClick={fetchInventoryMetrics}
      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 transition-colors flex items-center gap-1.5"
    >
      🔄 Refresh
    </button>
  );

  return (
    <>
    <HubLayout
      title="Inventory Hub"
      subtitle="Manage stock across all locations and channels"
      metrics={metrics}
      tabs={tabs}
      defaultTab="location"
      actions={actions}
    >
      {(activeTab) => (
        <>
          {activeTab === 'location' && (
            <div className="bg-slate-800/50 rounded-lg">
              <InventoryByLocation />
            </div>
          )}
          
          {activeTab === 'products' && (
            <div className="bg-slate-800/50 rounded-lg">
              <ProductMaster onEditProduct={handleEditProduct} />
            </div>
          )}
          
          {activeTab === 'overview' && (
            <div className="space-y-3">
              {/* Quick Stats - Compact MetricCards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <MetricCard
                  icon="📊"
                  label="TURNOVER"
                  value="0%"
                  subtitle="Last 30 days"
                  status="default"
                />
                <MetricCard
                  icon="⏱️"
                  label="AVG DAYS"
                  value="0"
                  subtitle="In stock"
                  status="default"
                />
                <MetricCard
                  icon="📈"
                  label="GROWTH"
                  value="0%"
                  subtitle="vs last month"
                  status="default"
                />
                <MetricCard
                  icon="🎯"
                  label="ACCURACY"
                  value="100%"
                  subtitle="Stock count"
                  status="green"
                />
              </div>

              {/* Location Distribution */}
              <div className="bg-slate-800 rounded-lg p-3">
                <h3 className="text-xs font-semibold text-white mb-3 flex items-center gap-1.5">
                  <span>📍</span> Location Distribution
                </h3>
                
                {/* Warning when no inventory data */}
                {inventoryData.total.qty === 0 && (
                  <div className="bg-yellow-900/30 border border-yellow-600/30 rounded-md p-2.5 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-yellow-400">⚠️</span>
                      <div className="text-xs text-yellow-300">
                        <strong>No inventory data.</strong> Add initial stock levels via &quot;By Location&quot; tab or sync from Veeqo.
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-slate-900 rounded-md p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-gray-400 uppercase">FBA</span>
                      <span className="text-xs font-bold text-white">{inventoryData.fba.qty}</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5">
                      <div 
                        className="bg-blue-500 h-1.5 rounded-full" 
                        style={{ width: `${inventoryData.total.qty > 0 ? (inventoryData.fba.qty / inventoryData.total.qty * 100) : 0}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1">{formatValue(inventoryData.fba.value)}</div>
                  </div>
                  <div className="bg-slate-900 rounded-md p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-gray-400 uppercase">AWD</span>
                      <span className="text-xs font-bold text-white">{inventoryData.awd.qty}</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5">
                      <div 
                        className="bg-green-500 h-1.5 rounded-full" 
                        style={{ width: `${inventoryData.total.qty > 0 ? (inventoryData.awd.qty / inventoryData.total.qty * 100) : 0}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1">{formatValue(inventoryData.awd.value)}</div>
                  </div>
                  <div className="bg-slate-900 rounded-md p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-gray-400 uppercase">GAR</span>
                      <span className="text-xs font-bold text-white">{inventoryData.gar.qty}</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5">
                      <div 
                        className="bg-yellow-500 h-1.5 rounded-full" 
                        style={{ width: `${inventoryData.total.qty > 0 ? (inventoryData.gar.qty / inventoryData.total.qty * 100) : 0}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1">{formatValue(inventoryData.gar.value)}</div>
                  </div>
                  <div className="bg-slate-900 rounded-md p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-gray-400 uppercase">OFF</span>
                      <span className="text-xs font-bold text-white">{inventoryData.off.qty}</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5">
                      <div 
                        className="bg-purple-500 h-1.5 rounded-full" 
                        style={{ width: `${inventoryData.total.qty > 0 ? (inventoryData.off.qty / inventoryData.total.qty * 100) : 0}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1">{formatValue(inventoryData.off.value)}</div>
                  </div>
                </div>
              </div>

              {/* Insights & Quick Actions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="bg-slate-800 rounded-lg p-3">
                  <h3 className="text-xs font-semibold text-white mb-2 flex items-center gap-1.5">
                    <span>💡</span> Inventory Insights
                  </h3>
                  <div className="space-y-1.5">
                    {inventoryData.lowStock === 0 ? (
                      <div className="flex items-start gap-2 text-[11px]">
                        <span className="text-green-400">✓</span>
                        <span className="text-gray-300">No critical stock issues detected</span>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 text-[11px]">
                        <span className="text-yellow-400">⚠️</span>
                        <span className="text-gray-300">
                          <span className="text-yellow-400 font-semibold">{inventoryData.lowStock}</span> SKUs running low on stock
                        </span>
                      </div>
                    )}
                    <div className="flex items-start gap-2 text-[11px]">
                      <span className="text-green-400">✓</span>
                      <span className="text-gray-300">Inventory distributed across {
                        [inventoryData.fba.qty > 0, inventoryData.awd.qty > 0, inventoryData.gar.qty > 0, inventoryData.off.qty > 0].filter(Boolean).length
                      } locations</span>
                    </div>
                    <div className="flex items-start gap-2 text-[11px]">
                      <span className="text-blue-400">ℹ️</span>
                      <span className="text-gray-300">
                        Total inventory value: <span className="text-white font-semibold">{formatValue(inventoryData.total.value)}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-lg p-3">
                  <h3 className="text-xs font-semibold text-white mb-2 flex items-center gap-1.5">
                    <span>⚡</span> Quick Actions
                  </h3>
                  <div className="space-y-1.5">
                    <button 
                      onClick={() => {}}
                      className="w-full text-left px-2.5 py-2 bg-slate-900 rounded text-xs text-gray-300 hover:bg-slate-700 hover:text-white transition-colors"
                    >
                      → Add Stock to Location
                    </button>
                    <button 
                      onClick={() => {}}
                      className="w-full text-left px-2.5 py-2 bg-slate-900 rounded text-xs text-gray-300 hover:bg-slate-700 hover:text-white transition-colors"
                    >
                      → Transfer Between Locations
                    </button>
                    <button 
                      onClick={() => {}}
                      className="w-full text-left px-2.5 py-2 bg-slate-900 rounded text-xs text-gray-300 hover:bg-slate-700 hover:text-white transition-colors"
                    >
                      → Export Inventory Report
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </HubLayout>
    
    {/* ============================================================ */}
    {/* Product Edit Modal - Rendered at parent level to prevent */}
    {/* state loss when ProductMaster re-renders */}
    {/* ============================================================ */}
    {editModalOpen && selectedProductId && (
      <ProductDetailPage
        open={editModalOpen}
        onClose={handleCloseEditModal}
        productId={selectedProductId}
        initialTab="general"
      />
    )}
  </>
  );
};

export default InventoryHub;
